import Layout from "@/components/layout/Layout";
import React, {
	useEffect,
	useState
} from "react";
import Content from "@/components/layout/Content";
import Heading from "@/components/elements/Heading";
import Sidepanel from "@/components/layout/Sidepanel";
import {
	InertiaLink,
	Link
} from "@inertiajs/inertia-react";
import DashboardMenu from "@/pages/Dashboard/DashboardMenu";
import ReactHtmlParser from "react-html-parser";
import { Inertia } from "@inertiajs/inertia";
import { getHeaders } from "@/services/api";

/**
 * Orders Dashboard Component
 *
 * Displays a paginated table of all orders in the system with search functionality.
 * Provides navigation to view individual orders and supports real-time search
 * with debounced API calls to filter results.
 *
 * @param {Object} props - Component props
 * @param {Object} props.orders - Paginated orders data from server
 * @param {Array} props.orders.data - Array of order objects
 * @param {Array} props.orders.links - Pagination links
 * @param {Object} props.orders.data[] - Individual order object
 * @param {string} props.orders.data[].id - Order ID
 * @param {string} props.orders.data[].created_at_nl - Formatted creation date
 * @param {string} props.orders.data[].description - Order reference/description
 * @param {number} props.orders.data[].products_count - Number of products in order
 * @param {number} props.orders.data[].reststatus - Order completion status (1=completed, 0=not completed)
 * @param {Object} props.orders.data[].contact - Order contact information
 * @param {Object} props.orders.data[].contact.company - Company details
 * @param {string} props.orders.data[].contact.company.name - Company name
 * @param {string} props.routeUrl - Base URL for search requests
 *
 * @returns {JSX.Element} The rendered orders dashboard page
 */
export default function Orders({ orders, routeUrl }) {
    // State for search debouncing and data management
    const [typingTimeout, setTimingTimeout] = useState(0);
    const [orderList, setOrderList] = useState(orders);
    const [search, setSearch] = useState("");

    /**
     * Initialize search state from URL parameters on component mount
     * Checks for 's' parameter in URL and sets search input accordingly
     */
    useEffect(() => {
        let queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        if (urlParams.has("s")) {
            setSearch(urlParams.get("s"));
        }
    }, []);

    /**
     * Handle search input changes with debouncing
     * Updates search state immediately and triggers API call after 500ms delay
     *
     * @param {Event} x - Input change event
     */
    function searchOrders(x) {
        let val = x.target.value;
        setSearch(val);

        // Clear existing timeout to restart debounce timer
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Set new timeout for delayed search request
        setTimingTimeout(
            setTimeout(() => {
                getOrders(val);
            }, 500),
        );
    }

    /**
     * Perform search API request
     * Makes GET request to filter orders based on search query
     *
     * @param {string} val - Search query value
     */
    function getOrders(val) {
        Inertia.get(routeUrl, { s: val }, {headers: getHeaders()});
    }

    return (
        <Layout>
            <Sidepanel>
                <DashboardMenu active={"Orders"} />
            </Sidepanel>
            <Content>
                <div
                    className={`contentoptions ingaveoptions col-md-12 d-flex flex-column`}
                >
                    <Heading heading="Orders">
                        <input
                            type="text"
                            onChange={searchOrders}
                            value={search || ""}
                            className="form-control"
                            style={{ maxWidth: 200 }}
                            placeholder="Zoeken"
                        />
                    </Heading>

                    <table className="table tablecustom tableadmin">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Besteldatum</th>
                                <th>Bedrijfsnaam</th>
                                <th>Referentie</th>
                                <th>Aantal producten</th>
                                <th>Rest status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderList.data.map((order, key) => {
                                return (
                                    <tr key={key}>
                                        <td>
                                            <InertiaLink
                                                name={"Bekijken"}
                                                href={`/dashboard/orders/${order.id}`}
                                                className={"mr-2"}
                                            >
                                                {order.id}
                                            </InertiaLink>
                                        </td>
                                        <td>
                                            <InertiaLink
                                                name={"Bekijken"}
                                                href={`/dashboard/orders/${order.id}`}
                                                className={"mr-2"}
                                            >
                                                {order.created_at_nl}
                                            </InertiaLink>
                                        </td>
                                        <td>
                                            <InertiaLink
                                                name={"Bekijken"}
                                                href={`/dashboard/orders/${order.id}`}
                                                className={"mr-2"}
                                            >
                                                {order.contact.company.name}
                                            </InertiaLink>
                                        </td>
                                        <td>
                                            <InertiaLink
                                                name={"Bekijken"}
                                                href={`/dashboard/orders/${order.id}`}
                                                className={"mr-2"}
                                            >
                                                {order.description}
                                            </InertiaLink>
                                        </td>
                                        <td>{order.products_count}</td>
                                        <td style={{ width: 30 }}>
                                            {order.reststatus === 1
                                                ? "Voltooid"
                                                : "Niet voltooid"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="d-flex justify-content-center">
                        {orderList.links.map((x, key) => {
                            return (
                                x.url && (
                                    <Link
                                        key={key}
                                        href={x.url}
                                        className="px-1"
                                    >
                                        {ReactHtmlParser(x.label)}
                                    </Link>
                                )
                            );
                        })}
                    </div>
                </div>
            </Content>
        </Layout>
    );
}
